(() => {
  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const parseJson = (element, fallback) => {
    if (!element) return fallback;
    try {
      return JSON.parse(element.textContent || '');
    } catch (_) {
      return fallback;
    }
  };

  const normalize = (value = '') => String(value).trim().toLowerCase().replace(/\s+/g, ' ');

  const textForProduct = (product) => {
    const tags = Array.isArray(product?.tags) ? product.tags.join(' ') : '';
    return normalize([product?.title, product?.type || product?.product_type, tags].filter(Boolean).join(' '));
  };

  const detectAge = (product, explicitAge = '') => {
    const explicit = normalize(explicitAge);
    if (explicit.includes('senior')) return 'senior';
    if (explicit.includes('intermediate')) return 'intermediate';
    if (explicit.includes('junior')) return 'junior';
    if (explicit.includes('youth')) return 'youth';

    const text = textForProduct(product);
    if (/\b(senior|sr)\b/.test(text)) return 'senior';
    if (/\b(intermediate|int)\b/.test(text)) return 'intermediate';
    if (/\b(junior|jr)\b/.test(text)) return 'junior';
    if (/\b(youth|yth|yt)\b/.test(text)) return 'youth';
    return '';
  };

  const detectRole = (product) => {
    const text = textForProduct(product);
    const type = normalize(product?.type || product?.product_type || '');
    const tags = Array.isArray(product?.tags) ? product.tags.map(normalize).join(' ') : '';
    const isBaseball = /\b(baseball|softball|fastpitch|slowpitch|usssa|bbcor)\b/.test(text) || /baseball|softball/.test(type);
    const isGoalie = /\bgoalie\b/.test(text) || /goalie/.test(type);

    if ((/runner|steel/.test(text) || /skate runners/.test(tags)) && /skate/.test(text + ' ' + tags)) return 'skate-steel';
    if (/bat grip|grip tape|bat taper/.test(text) || /bat accessories/.test(tags)) return 'bat-grip';
    if (/batting helmet/.test(text) && !/facemask|face mask|jaw guard|holder/.test(text)) return 'batting-helmet';
    if (/backpack|equipment bag|wheeled bag|bat pack|bat bag/.test(text) && isBaseball) return 'baseball-bag';
    if ((/conditioner|glove butter|glove treatment|glove mallet|glove band|glove clip/.test(text) || /glove accessories/.test(tags)) && isBaseball) return 'glove-care';

    if (isBaseball) {
      if (/\bbatting gloves?\b/.test(text)) return 'batting-gloves';
      if (/\bcatcher(?:'s)? mitt\b/.test(text)) return 'baseball-catcher-mitt';
      if (/\b(first base|1st base).*(mitt|glove)\b/.test(text) || /\b(first base|1st base) mitt\b/.test(text)) return 'first-base-mitt';
      if (/\b(glove|mitt)\b/.test(text) && !/batting/.test(text)) return 'fielding-glove';
      if (/\bbat\b/.test(text)) {
        if (/fastpitch/.test(text)) return 'fastpitch-bat';
        if (/slowpitch/.test(text)) return 'slowpitch-bat';
        if (/bbcor/.test(text)) return 'bbcor-bat';
        if (/usssa/.test(text)) return 'usssa-bat';
        if (/\busa\b/.test(text)) return 'usa-bat';
        return 'baseball-bat';
      }
      if (/catcher/.test(text) && /helmet|mask/.test(text)) return 'catcher-helmet';
      if (/catcher/.test(text) && /chest/.test(text)) return 'catcher-chest';
      if (/catcher/.test(text) && /leg/.test(text)) return 'catcher-leg';
      if (/\b(cleats?|shoes?|footwear)\b/.test(text)) return 'baseball-footwear';
    }

    if (isGoalie) {
      if (/\b(catcher|trapper)\b/.test(text)) return 'goalie-catcher';
      if (/\bblocker\b/.test(text)) return 'goalie-blocker';
      if (/\b(leg pads?|goal pads?)\b/.test(text)) return 'goalie-leg-pads';
      if (text.includes('chest & arm') || text.includes('chest and arm') || text.includes('goalie chest')) return 'goalie-chest';
      if (/\b(mask|helmet)\b/.test(text)) return 'goalie-mask';
      if (/\bskates?\b/.test(text)) return 'goalie-skates';
      if (/\bsticks?\b/.test(text) || text.includes('goal stick')) return 'goalie-stick';
      if (/\bpants?\b/.test(text)) return 'goalie-pants';
    }

    if (/\bshoulder pads?\b/.test(text)) return 'shoulder-pads';
    if (/\belbow pads?\b/.test(text)) return 'elbow-pads';
    if (/\b(shin guards?|shin pads?)\b/.test(text)) return 'shin-guards';
    if (/\bhockey pants?\b/.test(text)) return 'hockey-pants';
    if (/\bhockey gloves?\b/.test(text)) return 'hockey-gloves';
    if (/\bhockey helmets?\b/.test(text) || (/\bhelmet\b/.test(text) && /hockey/.test(text))) return 'hockey-helmet';

    if (/\bskates?\b/.test(text)) return 'hockey-skates';
    if (/\bsticks?\b/.test(text)) return 'hockey-stick';
    if (/\b(cleats?|shoes?|footwear)\b/.test(text)) return 'footwear';

    return '';
  };
  const roleQueries = {
    'goalie-catcher': 'goalie catcher',
    'goalie-blocker': 'goalie blocker',
    'goalie-leg-pads': 'goalie leg pads',
    'goalie-chest': 'goalie chest arm',
    'goalie-mask': 'goalie mask',
    'goalie-pants': 'goalie pants',
    'goalie-skates': 'goalie skates',
    'goalie-stick': 'goalie stick',
    'shoulder-pads': 'hockey shoulder pads',
    'elbow-pads': 'hockey elbow pads',
    'shin-guards': 'hockey shin guards',
    'hockey-pants': 'hockey pants',
    'hockey-gloves': 'hockey gloves',
    'hockey-helmet': 'hockey helmet',
    'hockey-skates': 'hockey skates',
    'hockey-stick': 'hockey stick',
    'fastpitch-bat': 'fastpitch softball bat',
    'slowpitch-bat': 'slowpitch softball bat',
    'bbcor-bat': 'BBCOR baseball bat',
    'usssa-bat': 'USSSA baseball bat',
    'usa-bat': 'USA baseball bat',
    'baseball-bat': 'baseball bat',
    'fielding-glove': 'baseball glove',
    'baseball-catcher-mitt': 'catcher mitt',
    'first-base-mitt': 'first base mitt',
    'batting-gloves': 'batting gloves',
    'catcher-helmet': 'catcher helmet',
    'catcher-chest': 'catcher chest protector',
    'catcher-leg': 'catcher leg guards',
    'baseball-footwear': 'baseball cleats',
    'footwear': 'athletic footwear',
    'skate-steel': 'Bauer FLY-X Hockey Skate Runners',
    'bat-grip': 'bat grip accessories',
    'batting-helmet': 'batting helmet',
    'baseball-bag': 'baseball backpack',
    'glove-care': 'glove accessories baseball'
  };
  const complementsByRole = {
    'goalie-catcher': ['goalie-blocker', 'goalie-leg-pads'],
    'goalie-blocker': ['goalie-catcher', 'goalie-leg-pads'],
    'goalie-leg-pads': ['goalie-catcher', 'goalie-blocker'],
    'catcher-helmet': ['catcher-chest', 'catcher-leg'],
    'catcher-chest': ['catcher-helmet', 'catcher-leg'],
    'catcher-leg': ['catcher-helmet', 'catcher-chest'],
    'hockey-skates': ['shoulder-pads', 'hockey-pants', 'hockey-gloves', 'shin-guards'],
    'shoulder-pads': ['elbow-pads', 'shin-guards', 'hockey-pants', 'hockey-gloves'],
    'elbow-pads': ['shoulder-pads', 'shin-guards', 'hockey-pants', 'hockey-gloves'],
    'shin-guards': ['shoulder-pads', 'elbow-pads', 'hockey-pants', 'hockey-gloves'],
    'hockey-pants': ['shoulder-pads', 'hockey-gloves', 'shin-guards', 'elbow-pads'],
    'hockey-gloves': ['shoulder-pads', 'hockey-pants', 'elbow-pads', 'shin-guards'],
    'hockey-stick': ['hockey-gloves', 'shoulder-pads', 'hockey-pants']
  };

  const BAUER_RUNNER_REQUIRED_HANDLES = new Set([
    'bauer-vapor-flylite-senior-hockey-skates',
    'bauer-vapor-flylite-red-senior-hockey-skates',
    'bauer-vapor-flylite-intermediate-hockey-skates',
    'bauer-vapor-flypro-senior-hockey-skates',
    'bauer-vapor-flypro-intermediate-hockey-skates',
    'bauer-supreme-fuse-senior-skate',
    'bauer-supreme-fuse-intermediate-hockey-skates',
    'bauer-supreme-f50-pro-senior-hockey-skates',
    'bauer-supreme-f50-pro-intermediate-hockey-skates',
    '1059765','1059766','1061682','1061683','1061681','1061726','1062089',
    'bauer-vapor-xltx-pro-plus-intermediate-hockey-skates-2023',
    'bauer-supreme-shadow-senior-hockey-skates',
    'bauer-supreme-shadow-intermediate-hockey-skates',
    'bauer-supreme-m50-pro-senior-hockey-skates',
    'bauer-supreme-m50-pro-intermediate-hockey-skates'
  ]);

  const accessoryRolesFor = (current, role) => {
    const handle = normalize(current?.handle);
    if (role === 'hockey-skates' && BAUER_RUNNER_REQUIRED_HANDLES.has(handle)) return ['skate-steel'];

    if (/^(fastpitch-bat|slowpitch-bat|bbcor-bat|usssa-bat|usa-bat|baseball-bat)$/.test(role || '')) {
      return ['batting-gloves', 'batting-helmet', 'bat-grip', 'baseball-bag'];
    }

    if (/^(fielding-glove|baseball-catcher-mitt|first-base-mitt)$/.test(role || '')) {
      return ['glove-care', 'batting-gloves', 'baseball-bag'];
    }

    if (/^(catcher-helmet|catcher-chest|catcher-leg)$/.test(role || '')) {
      return ['baseball-bag'];
    }

    return [];
  };

  const deriveFamily = (title = '') => {
    let value = normalize(title)
      .replace(/\(20\d{2}\)/g, ' ')
      .replace(/\b20\d{2}\b/g, ' ')
      .replace(/\bwith\s+(?:senior|intermediate|junior|youth)?\s*palm\b/g, ' ')
      .replace(/\b(?:60|70)\s*°/g, ' ')
      .replace(/\b(?:580|590)\b/g, ' ')
      .replace(/\bv90\b/g, ' ')
      .replace(/\b\d{2}(?:\.5)?\+\d(?:\.5)?\b/g, ' ')
      .replace(/\bteam\s+colou?rs?\b/g, ' ')
      .replace(/\bfull\s+right\b/g, ' ')
      .replace(/\bwhite\b/g, ' ')
      .replace(/\bregular\b/g, ' ')
      .replace(/\breg\b/g, ' ')
      .replace(/\b\d+\s*flex\b/g, ' ')
      .replace(/\b(senior|intermediate|junior|youth|sr|int|jr|yth|yt)\b/g, ' ')
      .replace(/\b(goalie\s+catcher|catcher|trapper|goalie\s+blocker|blocker|goalie\s+leg\s+pads?|leg\s+pads?|goal\s+pads?|chest\s*(?:&|and)\s*arm\s*protector|goalie\s+chest|hockey\s+shoulder\s+pads?|shoulder\s+pads?|hockey\s+elbow\s+pads?|elbow\s+pads?|hockey\s+shin\s+guards?|shin\s+guards?|shin\s+pads?|hockey\s+pants?|goalie\s+pants?|hockey\s+gloves?|goalie\s+gloves?|goalie\s+skates?|hockey\s+skates?|skates?|goalie\s+sticks?|hockey\s+sticks?|goal\s+sticks?|sticks?)\b/g, ' ')
      .replace(/\b(goalie|hockey)\b/g, ' ')
      .replace(/\bextreme\s+flex\b/g, 'eflex')
      .replace(/\be-?flex\b/g, 'eflex')
      .replace(/\beflex\s+e(?=\d)/g, 'eflex ')
      .replace(/\bvelocity\b/g, ' ')
      .replace(/\b(total\s+custom)\s+pro\b/g, '$1')
      .replace(/[^a-z0-9.]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return value;
  };

  const deriveSearchFamily = (title = '') => normalize(title)
    .replace(/\(20\d{2}\)/g, ' ')
    .replace(/\b20\d{2}\b/g, ' ')
    .replace(/\bwith\s+(?:senior|intermediate|junior|youth)?\s*palm\b/g, ' ')
    .replace(/\b(?:60|70)\s*°/g, ' ')
    .replace(/\b(?:580|590)\b/g, ' ')
    .replace(/\bv90\b/g, ' ')
    .replace(/\b\d{2}(?:\.5)?\+\d(?:\.5)?\b/g, ' ')
    .replace(/\bteam\s+colou?rs?\b/g, ' ')
    .replace(/\bfull\s+right\b/g, ' ')
    .replace(/\bwhite\b/g, ' ')
    .replace(/\bregular\b/g, ' ')
    .replace(/\breg\b/g, ' ')
    .replace(/\b\d+\s*flex\b/g, ' ')
    .replace(/\b(senior|intermediate|junior|youth|sr|int|jr|yth|yt)\b/g, ' ')
    .replace(/\b(goalie\s+catcher|catcher|trapper|goalie\s+blocker|blocker|goalie\s+leg\s+pads?|leg\s+pads?|goal\s+pads?|chest\s*(?:&|and)\s*arm\s*protector|goalie\s+chest|hockey\s+shoulder\s+pads?|shoulder\s+pads?|hockey\s+elbow\s+pads?|elbow\s+pads?|hockey\s+shin\s+guards?|shin\s+guards?|shin\s+pads?|hockey\s+pants?|goalie\s+pants?|hockey\s+gloves?|goalie\s+gloves?|goalie\s+skates?|hockey\s+skates?|skates?|goalie\s+sticks?|hockey\s+sticks?|goal\s+sticks?|sticks?)\b/g, ' ')
    .replace(/\b(goalie|hockey)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const ageLabel = (age) => ({
    senior: 'Senior',
    intermediate: 'Intermediate',
    junior: 'Junior',
    youth: 'Youth'
  }[age] || '');

  const isUsedProduct = (product) => {
    const title = normalize(product?.title);
    const type = normalize(product?.product_type || product?.type);
    const firstWord = title.split(/\s+/)[0] || '';
    const tags = Array.isArray(product?.tags) ? product.tags : [];
    return firstWord === 'used'
      || title.includes(' used')
      || title.includes('-u')
      || type.includes('used')
      || tags.some((tag) => {
        const value = normalize(tag);
        return value.includes('used') || value.includes('trade-in');
      });
  };

  const validCandidate = (product, currentId) => {
    if (!product) return false;
    if (String(product.id) === String(currentId)) return false;
    if (product.available === false) return false;
    if (isUsedProduct(product)) return false;
    return true;
  };

  const matchesAge = (product, currentAge) => {
    if (!currentAge) return true;
    const candidateAge = detectAge(product);
    return !candidateAge || candidateAge === currentAge;
  };

  const fetchIntent = async (productId, intent) => {
    const root = window.Shopify?.routes?.root || '/';
    const url = root + 'recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=10&intent=' + encodeURIComponent(intent);
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  };

  const searchCache = new Map();

  const fetchSearchProducts = async (query) => {
    if (!query) return [];
    if (searchCache.has(query)) return searchCache.get(query);

    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '6');
    params.set('resources[options][unavailable_products]', 'hide');
    params.set('resources[options][fields]', 'title,product_type,tag,vendor');

    const request = fetch(root + 'search/suggest.json?' + params.toString(), {
      headers: { Accept: 'application/json' }
    })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => data?.resources?.results?.products || [])
      .catch(() => []);

    searchCache.set(query, request);
    return request;
  };

  const fetchFamilyRole = async (family, searchFamily, age, role) => {
    if (!family || !roleQueries[role]) return [];

    const primaryQuery = [searchFamily || family, ageLabel(age), roleQueries[role]].filter(Boolean).join(' ');
    const fallbackQuery = [family, ageLabel(age), roleQueries[role]].filter(Boolean).join(' ');

    const primary = await fetchSearchProducts(primaryQuery);
    if (primary.some((product) => deriveFamily(product.title) === family)) return primary;
    if (fallbackQuery === primaryQuery) return primary;

    const fallback = await fetchSearchProducts(fallbackQuery);
    const seen = new Set();
    return primary.concat(fallback).filter((product) => {
      const key = String(product?.id || product?.handle || product?.url || '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const accessoryQueryFor = (current, role, age) => {
    const batClass = detectBatClass(current);

    if (role === 'skate-steel') return 'Bauer FLY-X Hockey Skate Runners';
    if (role === 'batting-gloves') {
      if (batClass === 'fastpitch') return [ageLabel(age), 'fastpitch batting gloves'].filter(Boolean).join(' ');
      return [ageLabel(age), 'batting gloves'].filter(Boolean).join(' ');
    }
    if (role === 'batting-helmet') {
      if (batClass === 'fastpitch') return 'fastpitch batting helmet';
      if (batClass === 'tball') return 'T-Ball batting helmet';
      return 'batting helmet';
    }
    if (role === 'bat-grip') return 'bat grip accessories';
    if (role === 'baseball-bag') return 'baseball backpack';
    if (role === 'glove-care') return 'glove accessories baseball';
    return roleQueries[role] || '';
  };

  const accessoryCandidateScore = (product, role, current, age) => {
    if (!validCandidate(product, current?.id)) return -1;
    if (detectRole(product) !== role) return -1;

    let score = 100;
    const title = titleText(product);
    const tags = Array.isArray(product?.tags) ? product.tags.map(normalize).join(' ') : '';

    if (role === 'skate-steel') {
      if (!BAUER_RUNNER_REQUIRED_HANDLES.has(normalize(current?.handle))) return -1;
      if (/fly-x/.test(title)) score += 35;
      if (/fly-ti/.test(title)) score += 25;
      if (!/bauer/.test(title + ' ' + normalize(product?.vendor))) return -1;
    }

    if (role === 'batting-gloves') {
      const batClass = detectBatClass(current);
      if (batClass === 'fastpitch' && /fastpitch|women/.test(title + ' ' + tags)) score += 28;
      const candidateAge = detectAge(product);
      if (age && candidateAge === age) score += 18;
    }

    if (role === 'batting-helmet') {
      const batClass = detectBatClass(current);
      if (batClass === 'fastpitch' && /fastpitch|softball/.test(title + ' ' + tags)) score += 28;
      if (batClass === 'tball' && /t-ball|tball/.test(title + ' ' + tags)) score += 28;
      if (/facemask|face mask|jaw guard|holder/.test(title)) return -1;
    }

    if (role === 'bat-grip') {
      if (/lizard skins/.test(title)) score += 12;
      if (/grip/.test(title)) score += 18;
      if (/taper/.test(title)) score += 8;
    }

    if (role === 'glove-care') {
      if (/conditioner|butter|treatment/.test(title)) score += 35;
      else if (/glove band/.test(title)) score += 22;
      else if (/mallet/.test(title)) score += 18;
      else if (/clip/.test(title)) score += 8;
      if (/inner glove/.test(title)) score -= 4;
    }

    if (role === 'baseball-bag') {
      if (/backpack|wheeled/.test(title)) score += 20;
      if (/coach|bucket/.test(title)) score -= 20;
    }

    return score;
  };

  const fetchAccessoryRole = async (current, role, age) => {
    const query = accessoryQueryFor(current, role, age);
    if (!query) return [];

    const primary = await fetchSearchProducts(query);
    let products = primary;

    if (role === 'skate-steel') {
      const ti = await fetchSearchProducts('Bauer FLY-TI Hockey Skate Runners');
      products = mergeUniqueProducts(primary, ti);
    }

    return products
      .map(product => ({ product, score: accessoryCandidateScore(product, role, current, age) }))
      .filter(item => item.score >= 0)
      .sort((a, b) => b.score - a.score)
      .map(item => item.product);
  };

  const recommendationMeta = (current, currentRole, targetRole, kind) => {
    const age = detectAge(current, current?.age_group);
    const ageText = ageLabel(age);

    if (kind === 'set') {
      return {
        label: 'MATCHING SET',
        reason: ['Same product family', ageText ? 'matching ' + ageText + ' level' : 'complementary piece'].filter(Boolean).join(' · ')
      };
    }

    const map = {
      'skate-steel': ['COMPATIBLE STEEL', 'Compatible runner option for this Bauer skate setup'],
      'batting-gloves': ['PAIR WITH THIS BAT', 'Adds grip and hand protection at the plate'],
      'batting-helmet': ['GAME READY', 'Batting helmet for the same baseball or softball setup'],
      'bat-grip': ['BAT ACCESSORY', 'Grip or taper option to refresh bat feel and control'],
      'baseball-bag': ['GEAR STORAGE', 'Useful storage for the same baseball or softball setup'],
      'glove-care': ['GLOVE CARE', 'Useful for break-in, shape retention or leather care'],
      'catcher-helmet': ['CATCHER SET', ageText ? 'Matching ' + ageText + ' catcher protection' : 'Completes catcher protection'],
      'catcher-chest': ['CATCHER SET', ageText ? 'Matching ' + ageText + ' catcher protection' : 'Completes catcher protection'],
      'catcher-leg': ['CATCHER SET', ageText ? 'Matching ' + ageText + ' catcher protection' : 'Completes catcher protection'],
      'goalie-catcher': ['MATCHING SET', 'Matching catcher for the goalie set'],
      'goalie-blocker': ['MATCHING SET', 'Matching blocker for the goalie set'],
      'goalie-leg-pads': ['MATCHING SET', 'Matching leg pads for the goalie set'],
      'shoulder-pads': ['MATCHING LEVEL', ageText ? ageText + ' protective option' : 'Complementary protective gear'],
      'elbow-pads': ['MATCHING LEVEL', ageText ? ageText + ' protective option' : 'Complementary protective gear'],
      'shin-guards': ['MATCHING LEVEL', ageText ? ageText + ' protective option' : 'Complementary protective gear'],
      'hockey-pants': ['MATCHING LEVEL', ageText ? ageText + ' protective option' : 'Complementary protective gear'],
      'hockey-gloves': ['MATCHING LEVEL', ageText ? ageText + ' glove option' : 'Complementary hockey gear']
    };

    const item = map[targetRole] || ['PAIRS WITH THIS', 'Useful complementary gear'];
    return { label: item[0], reason: item[1] };
  };

  const imageUrl = (product) => {
    const image = product?.featured_image;
    const raw = typeof image === 'string' ? image : image?.url || image?.src || product?.images?.[0]?.src || product?.images?.[0] || '';
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.origin);
      url.searchParams.set('width', '720');
      return url.toString();
    } catch (_) {
      return raw;
    }
  };

  const formatMoney = (rawValue, currency) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') return '';

    const numeric = Number(rawValue);
    if (!Number.isFinite(numeric)) return '';

    const raw = String(rawValue);
    const cents = raw.includes('.') ? Math.round(numeric * 100) : numeric;
    const locale = document.documentElement.lang || 'en-CA';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (_) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  const centsValue = (rawValue) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') return 0;
    const numeric = Number(rawValue);
    if (!Number.isFinite(numeric)) return 0;
    return String(rawValue).includes('.') ? Math.round(numeric * 100) : Math.round(numeric);
  };

  const priceCents = (product) => centsValue(product?.price_min ?? product?.price ?? 0);
  const compareAtCents = (product) => centsValue(product?.compare_at_price_max ?? product?.compare_at_price ?? 0);

  const safePricePair = (product) => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (variants.length) {
      const priced = variants
        .map((variant) => ({
          price: centsValue(variant?.price),
          compare: centsValue(variant?.compare_at_price)
        }))
        .filter((entry) => entry.price > 0)
        .sort((a, b) => a.price - b.price);

      if (priced.length) {
        const cheapest = priced[0];
        return {
          price: cheapest.price,
          compare: cheapest.compare > cheapest.price ? cheapest.compare : 0
        };
      }
    }

    const price = priceCents(product);
    const fixedPricing = product?.price_varies === false && product?.compare_at_price_varies === false;
    const compare = fixedPricing ? compareAtCents(product) : 0;
    return {
      price,
      compare: compare > price ? compare : 0
    };
  };

  const brandMatches = (product, brand) => {
    const needle = normalize(brand);
    if (!needle) return false;
    const title = normalize(product?.title);
    const vendor = normalize(product?.vendor);
    return title.includes(needle) || vendor === needle || vendor.includes(needle);
  };

  const sameProductType = (candidate, current) => {
    const a = normalize(candidate?.type || candidate?.product_type);
    const b = normalize(current?.type || current?.product_type);
    return Boolean(a && b && a === b);
  };

  const titleText = (product) => normalize(product?.title || '');

  const parseBatDrop = (product) => {
    const text = String(product?.title || '');
    const matches = [...text.matchAll(/(?:^|\s)-(\d{1,2})(?=\s|$|[^0-9])/g)];
    if (!matches.length) return 0;
    return Number(matches[matches.length - 1][1]) || 0;
  };

  const parseBarrel = (product) => {
    const text = normalize(product?.title || '');
    if (/2\s*3\/4|2\.75/.test(text)) return '2 3/4';
    if (/2\s*5\/8|2\.625/.test(text)) return '2 5/8';
    if (/2\s*1\/4|2\.25/.test(text)) return '2 1/4';
    return '';
  };

  const detectBatClass = (product) => {
    const text = textForProduct(product);
    if (/\bfastpitch\b/.test(text)) return 'fastpitch';
    if (/\bslowpitch\b/.test(text)) return 'slowpitch';
    if (/\bbbcor\b/.test(text)) return 'bbcor';
    if (/\busssa\b/.test(text) || /junior big barrel|\bjbb\b/.test(text)) return 'usssa';
    if (/\busa\b/.test(text)) return 'usa';
    if (/\bt-?ball\b|\btee ball\b/.test(text)) return 'tball';
    return '';
  };

  const detectGlovePosition = (product) => {
    const text = textForProduct(product);
    if (/catcher/.test(text)) return 'catcher';
    if (/first base|1st base/.test(text)) return 'first-base';
    if (/outfield/.test(text)) return 'outfield';
    if (/infield/.test(text)) return 'infield';
    if (/pitcher/.test(text)) return 'pitcher';
    if (/utility/.test(text)) return 'utility';
    return '';
  };

  const parseGloveLength = (product) => {
    const text = String(product?.title || '');
    const match = text.match(/\b(9|10|10\.5|11|11\.25|11\.5|11\.75|12|12\.25|12\.5|12\.75|13|13\.5|14|31|31\.5|32|32\.5|33|33\.5|34|34\.5|35)s*["”]/);
    return match ? Number(match[1]) : 0;
  };

  const detectSoftballStyle = (product) => {
    const text = textForProduct(product);
    if (/fastpitch/.test(text)) return 'fastpitch';
    if (/slowpitch/.test(text)) return 'slowpitch';
    return /softball/.test(text) ? 'softball' : 'baseball';
  };

  const detectHockeyLine = (product) => {
    const text = titleText(product);
    const lines = [
      'supreme','vapor','nexus','tacks','jetspeed','ribcor','covert','alpha','novium',
      'rekker','code','hazrdus','catalyst','axis','eflex','extreme flex','velocity','ventus','gsx'
    ];
    return lines.find(line => text.includes(line)) || '';
  };

  const productYear = (product) => {
    const match = String(product?.title || '').match(/\b(20\d{2})\b/);
    return match ? Number(match[1]) : 0;
  };

  const valuesForOption = (product, matcher) => {
    if (product?.option_values && typeof product.option_values === 'object') {
      const found = Object.entries(product.option_values).find(([name]) => matcher.test(normalize(name)));
      if (found) return new Set((Array.isArray(found[1]) ? found[1] : [found[1]]).map(normalize).filter(Boolean));
    }

    const optionNames = Array.isArray(product?.options)
      ? product.options.map(option => typeof option === 'string' ? option : option?.name).filter(Boolean)
      : [];

    if (!optionNames.length || !Array.isArray(product?.variants)) return new Set();
    const index = optionNames.findIndex(name => matcher.test(normalize(name)));
    if (index < 0) return new Set();

    const values = new Set();
    product.variants.forEach(variant => {
      const value = variant?.['option' + (index + 1)] ?? variant?.options?.[index];
      if (value && normalize(value) !== 'default title') values.add(normalize(value));
    });
    return values;
  };

  const optionOverlap = (a, b, matcher) => {
    const left = valuesForOption(a, matcher);
    const right = valuesForOption(b, matcher);
    if (!left.size || !right.size) return 0;
    let overlap = 0;
    left.forEach(value => { if (right.has(value)) overlap += 1; });
    return overlap / Math.min(left.size, right.size);
  };

  const categorySimilarityScore = (candidate, current, role) => {
    let score = 0;
    const currentTitle = titleText(current);
    const candidateTitle = titleText(candidate);

    if (/bat$|bat'|batting|bbcor|usssa|fastpitch-bat|slowpitch-bat|baseball-bat|usa-bat/.test(role || '')) {
      const currentClass = detectBatClass(current);
      const candidateClass = detectBatClass(candidate);
      if (currentClass && candidateClass) score += currentClass === candidateClass ? 45 : -80;

      const currentDrop = parseBatDrop(current);
      const candidateDrop = parseBatDrop(candidate);
      if (currentDrop && candidateDrop) {
        if (currentDrop === candidateDrop) score += 52;
        else if (Math.abs(currentDrop - candidateDrop) <= 2) score += 8;
        else score -= 35;
      }

      const currentBarrel = parseBarrel(current);
      const candidateBarrel = parseBarrel(candidate);
      if (currentBarrel && candidateBarrel) score += currentBarrel === candidateBarrel ? 16 : -8;
    }

    if (role === 'fielding-glove' || role === 'baseball-catcher-mitt' || role === 'first-base-mitt') {
      const currentStyle = detectSoftballStyle(current);
      const candidateStyle = detectSoftballStyle(candidate);
      if (currentStyle && candidateStyle) score += currentStyle === candidateStyle ? 35 : -55;

      const currentPosition = detectGlovePosition(current);
      const candidatePosition = detectGlovePosition(candidate);
      if (currentPosition && candidatePosition) score += currentPosition === candidatePosition ? 38 : -22;

      const currentLength = parseGloveLength(current);
      const candidateLength = parseGloveLength(candidate);
      if (currentLength && candidateLength) {
        const gap = Math.abs(currentLength - candidateLength);
        if (gap <= .25) score += 32;
        else if (gap <= .5) score += 22;
        else if (gap <= 1) score += 8;
        else score -= 16;
      }
    }

    if (role === 'hockey-skates' || role === 'goalie-skates') {
      const lineA = detectHockeyLine(current);
      const lineB = detectHockeyLine(candidate);
      if (lineA && lineB && lineA === lineB) score += 18;
      score += Math.round(optionOverlap(current, candidate, /fit|width/) * 22);
      score += Math.round(optionOverlap(current, candidate, /size/) * 12);
    }

    if (role === 'hockey-stick' || role === 'goalie-stick') {
      const lineA = detectHockeyLine(current);
      const lineB = detectHockeyLine(candidate);
      if (lineA && lineB && lineA === lineB) score += 14;
      score += Math.round(optionOverlap(current, candidate, /flex/) * 26);
      score += Math.round(optionOverlap(current, candidate, /curve|pattern/) * 12);
      score += Math.round(optionOverlap(current, candidate, /hand/) * 8);
    }

    if (role === 'hockey-gloves' || role === 'goalie-catcher' || role === 'goalie-blocker') {
      score += Math.round(optionOverlap(current, candidate, /size/) * 18);
      score += Math.round(optionOverlap(current, candidate, /hand/) * 10);
    }

    if (role === 'goalie-leg-pads' || role === 'goalie-chest' || role === 'goalie-pants' || role === 'shoulder-pads' || role === 'elbow-pads' || role === 'shin-guards' || role === 'hockey-pants') {
      score += Math.round(optionOverlap(current, candidate, /size/) * 18);
      const lineA = detectHockeyLine(current);
      const lineB = detectHockeyLine(candidate);
      if (lineA && lineB && lineA === lineB) score += 10;
    }

    const yearA = productYear(current);
    const yearB = productYear(candidate);
    if (yearA && yearB) {
      const gap = Math.abs(yearA - yearB);
      if (gap === 0) score += 10;
      else if (gap === 1) score += 6;
      else if (gap >= 4) score -= 12;
    } else if (/\b(2016|2017|2018|2019|2020|2021)\b/.test(candidateTitle) && !/\b(2016|2017|2018|2019|2020|2021)\b/.test(currentTitle)) {
      score -= 10;
    }

    return score;
  };

  const compareScore = (candidate, current, role, age) => {
    if (!validCandidate(candidate, current?.id)) return -1;

    const candidateRole = detectRole(candidate);
    if (role) {
      if (candidateRole !== role) return -1;
    } else if (!sameProductType(candidate, current)) {
      return -1;
    }

    const candidateAge = detectAge(candidate);
    if (age && candidateAge && candidateAge !== age) return -1;

    let score = role ? 120 : 70;
    if (age && candidateAge === age) score += 38;
    if (brandMatches(candidate, current?.brand)) score += (role === 'hockey-skates' || role === 'goalie-skates') ? 38 : 24;

    const currentFamily = deriveFamily(current?.title || '');
    const candidateFamily = deriveFamily(candidate?.title || '');
    if (currentFamily && candidateFamily && currentFamily !== candidateFamily) score += 6;

    const currentPrice = priceCents(current);
    const candidatePrice = priceCents(candidate);
    if (currentPrice > 0 && candidatePrice > 0) {
      const ratio = candidatePrice / currentPrice;
      if (ratio >= .78 && ratio <= 1.28) score += 34;
      else if (ratio >= .62 && ratio <= 1.6) score += 24;
      else if (ratio >= .48 && ratio <= 1.9) score += 10;
      else score -= 22;
    }

    score += categorySimilarityScore(candidate, current, role);
    return score;
  };
  const mergeUniqueProducts = (...groups) => {
    const seen = new Set();
    return groups.flat().filter((product) => {
      const key = String(product?.id || product?.handle || product?.url || '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const handleFromProduct = (product) => {
    if (product?.handle) return product.handle;
    const match = String(product?.url || '').match(/\/products\/([^/?#]+)/);
    return match?.[1] || '';
  };

  const fetchProductDetail = async (product) => {
    const handle = handleFromProduct(product);
    if (!handle) return product;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return product;
      const detail = await response.json();
      return Object.assign({}, product, detail, {
        _szBrand: product?._szBrand || ''
      });
    } catch (_) {
      return product;
    }
  };

  const optionCounts = (product) => {
    if (product?.option_counts && typeof product.option_counts === 'object') {
      return Object.entries(product.option_counts)
        .map(([name, count]) => ({ name, count: Number(count) || 0 }))
        .filter(item => item.count > 0);
    }

    const optionNames = Array.isArray(product?.options)
      ? product.options.map(option => typeof option === 'string' ? option : option?.name).filter(Boolean)
      : [];

    if (!optionNames.length || !Array.isArray(product?.variants)) return [];

    return optionNames.map((name, index) => {
      const values = new Set();
      product.variants.forEach(variant => {
        const value = variant?.['option' + (index + 1)] ?? variant?.options?.[index];
        if (value && normalize(value) !== 'default title') values.add(String(value));
      });
      return { name, count: values.size };
    }).filter(item => item.count > 0);
  };

  const keyOptionFacts = (product) => {
    const preferred = /size|fit|flex|curve|hand|width|length|weight|colour|color|drop|barrel/;
    const counts = optionCounts(product);
    const primary = counts.filter(item => preferred.test(normalize(item.name)));
    const source = primary.length ? primary : counts;
    return source.slice(0, 2).map(item => {
      const name = normalize(item.name);
      let label = item.name;
      if (name === 'color' || name === 'colour') label = 'colours';
      else if (!/s$/i.test(label)) label += 's';
      return item.count + ' ' + label;
    });
  };

  const availableVariantCount = (product) => {
    if (Number.isFinite(Number(product?.available_variants))) return Number(product.available_variants);
    if (Array.isArray(product?.variants)) return product.variants.filter(variant => variant?.available !== false).length;
    return 0;
  };

  const compareImageUrl = (product) => {
    const image = product?.featured_image;
    const raw = typeof image === 'string'
      ? image
      : image?.url || image?.src || product?.images?.[0]?.src || product?.images?.[0] || '';
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.origin);
      url.searchParams.set('width', '720');
      return url.toString();
    } catch (_) {
      return raw;
    }
  };

  const compareValueLabel = (product, current, allProducts, isCurrent, currency) => {
    const pair = safePricePair(product);
    const price = pair.price;
    const currentPrice = priceCents(current);

    if (isCurrent && pair.compare > price) {
      return 'Save ' + formatMoney(pair.compare - price, currency);
    }

    if (!isCurrent && price > 0 && currentPrice > 0) {
      const delta = Math.abs(price - currentPrice);
      if (price < currentPrice) return formatMoney(delta, currency) + ' less';
      if (price > currentPrice) return '+' + formatMoney(delta, currency);
      return 'Same price';
    }

    const prices = allProducts.map(priceCents).filter(Boolean);
    if (isCurrent && prices.length > 1 && price === Math.min(...prices)) return 'Lowest price here';
    if (isCurrent && prices.length > 1 && price === Math.max(...prices)) return 'Premium price point';
    return 'Similar model';
  };

  const comparisonReason = (product, current, role, isCurrent) => {
    if (isCurrent) return 'Reference model';

    const reasons = [];
    if (detectAge(current) && detectAge(product) === detectAge(current)) reasons.push(ageLabel(detectAge(current)));

    if (/bat/.test(role || '')) {
      const batClass = detectBatClass(current);
      const drop = parseBatDrop(current);
      if (batClass && detectBatClass(product) === batClass) reasons.push(batClass === 'tball' ? 'T-Ball' : batClass.toUpperCase());
      if (drop && parseBatDrop(product) === drop) reasons.push('drop -' + drop);
    } else if (role === 'fielding-glove' || role === 'baseball-catcher-mitt' || role === 'first-base-mitt') {
      const position = detectGlovePosition(current);
      const length = parseGloveLength(current);
      if (position && detectGlovePosition(product) === position) reasons.push(position.replace('-', ' '));
      if (length && parseGloveLength(product) === length) reasons.push(length + '"');
    } else if (role === 'hockey-skates' || role === 'goalie-skates') {
      if (brandMatches(product, current?.brand)) reasons.push(current?.brand || 'same brand');
      const line = detectHockeyLine(current);
      if (line && detectHockeyLine(product) === line) reasons.push(line);
    } else if (role === 'hockey-stick' || role === 'goalie-stick') {
      if (optionOverlap(current, product, /flex/) > 0) reasons.push('overlapping flex');
      const line = detectHockeyLine(current);
      if (line && detectHockeyLine(product) === line) reasons.push(line);
    } else if (brandMatches(product, current?.brand)) {
      reasons.push(current?.brand || 'same brand');
    }

    return reasons.slice(0, 2).join(' · ') || 'Same category';
  };

  const pricePositionLabel = (product, current, isCurrent) => {
    if (isCurrent) return 'YOU\'RE VIEWING';
    const price = priceCents(product);
    const currentPrice = priceCents(current);
    if (!price || !currentPrice) return 'SIMILAR MODEL';
    if (price < currentPrice) return 'LOWER PRICE';
    if (price > currentPrice) return 'HIGHER PRICE';
    return 'SIMILAR PRICE';
  };

  const compareCardMarkup = (product, current, allProducts, isCurrent, currency, role) => {
    const root = window.Shopify?.routes?.root || '/';
    const url = product?.url || root + 'products/' + handleFromProduct(product);
    const image = compareImageUrl(product);
    const pair = safePricePair(product);
    const price = pair.price;
    const compareAt = pair.compare;
    const facts = keyOptionFacts(product);
    const inStock = availableVariantCount(product);
    const brand = product?._szBrand || (isCurrent ? current?.brand : '') || '';
    const value = compareValueLabel(product, current, allProducts, isCurrent, currency);
    const reason = comparisonReason(product, current, role, isCurrent);
    const label = pricePositionLabel(product, current, isCurrent);
    const saleSaving = compareAt > price ? compareAt - price : 0;

    return '<article class="sz-compare-card' + (isCurrent ? ' sz-compare-card--current' : '') + '" role="listitem">' +
      '<div class="sz-compare-card__top">' +
        '<span class="sz-compare-card__label">' + label + '</span>' +
        '<span class="sz-compare-card__value">' + escapeHtml(value) + '</span>' +
      '</div>' +
      '<div class="sz-compare-card__image-wrap">' +
        (image
          ? '<img class="sz-compare-card__image" src="' + escapeHtml(image) + '" alt="' + escapeHtml(product?.title || '') + '" loading="lazy" width="720" height="720">'
          : '<div class="sz-gear-match__image-empty" aria-hidden="true"><img class="sz-placeholder-logo" src="/cdn/shop/files/logo.png" alt="" loading="lazy" width="850" height="244"><strong>Product image coming soon</strong></div>') +
      '</div>' +
      '<div class="sz-compare-card__body">' +
        (brand ? '<p class="sz-compare-card__brand">' + escapeHtml(brand) + '</p>' : '') +
        '<h3 class="sz-compare-card__title">' + escapeHtml(product?.title || '') + '</h3>' +
        '<span class="sz-compare-card__reason">' + escapeHtml(reason) + '</span>' +
        '<div class="sz-compare-card__prices"><strong>' + formatMoney(price, currency) + '</strong>' +
          (centsValue(compareAt) > centsValue(price) ? '<s>' + formatMoney(compareAt, currency) + '</s>' : '') +
        '</div>' +
        ((facts.length || saleSaving > 0) ? '<div class="sz-compare-card__facts">' +
          (saleSaving > 0 ? '<span class="sz-compare-card__fact sz-compare-card__fact--sale">Save ' + escapeHtml(formatMoney(saleSaving, currency)) + '</span>' : '') +
          facts.map(fact => '<span class="sz-compare-card__fact">' + escapeHtml(fact) + '</span>').join('') +
        '</div>' : '') +
        (inStock ? '<span class="sz-compare-card__stock">' + inStock + ' in-stock option' + (inStock === 1 ? '' : 's') + '</span>' : '') +
        (isCurrent ? '<span class="sz-compare-card__cta">Current choice</span>' : '<a class="sz-compare-card__cta" href="' + escapeHtml(url) + '">View model <span aria-hidden="true">→</span></a>') +
      '</div>' +
    '</article>';
  };

  const compareQueryFor = (current, role, age, includeBrand = true) => {
    const category = roleQueries[role] || current?.type || current?.product_type || '';
    const extras = [];

    if (/bat/.test(role || '')) {
      const batClass = detectBatClass(current);
      const drop = parseBatDrop(current);
      if (batClass) extras.push(batClass === 'tball' ? 'T-Ball' : batClass.toUpperCase());
      if (drop) extras.push('-' + drop);
    }

    if (role === 'fielding-glove' || role === 'baseball-catcher-mitt' || role === 'first-base-mitt') {
      const position = detectGlovePosition(current);
      const length = parseGloveLength(current);
      const style = detectSoftballStyle(current);
      if (style === 'fastpitch' || style === 'slowpitch') extras.push(style);
      if (position) extras.push(position.replace('-', ' '));
      if (length) extras.push(length + '"');
    }

    return [includeBrand ? current?.brand : '', ageLabel(age), category, ...extras].filter(Boolean).join(' ');
  };
  const cardMarkup = (product, kind, index, currency, role, current, currentRole) => {
    const root = window.Shopify?.routes?.root || '/';
    const url = product.url || root + 'products/' + product.handle;
    const image = imageUrl(product);
    const pair = safePricePair(product);
    const price = pair.price;
    const compareAt = pair.compare;
    const number = String(index + 1).padStart(2, '0');
    const brand = product._szBrand || '';
    const meta = recommendationMeta(current, currentRole, role || detectRole(product), kind);

    return '<article class="sz-gear-match__item" role="listitem">' +
      '<a class="sz-gear-match__card" href="' + escapeHtml(url) + '">' +
        '<div class="sz-gear-match__card-top">' +
          '<span class="sz-gear-match__match sz-gear-match__match--pair">' + escapeHtml(meta.label) + '</span>' +
          '<span class="sz-gear-match__number">' + number + '</span>' +
        '</div>' +
        '<div class="sz-gear-match__image-wrap">' +
          (image
            ? '<img class="sz-gear-match__image" src="' + escapeHtml(image) + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="720" height="720">'
            : '<div class="sz-gear-match__image-empty" aria-hidden="true"><img class="sz-placeholder-logo" src="/cdn/shop/files/logo.png" alt="" loading="lazy" width="850" height="244"><strong>Product image coming soon</strong></div>') +
        '</div>' +
        '<div class="sz-gear-match__content">' +
          (brand ? '<p class="sz-gear-match__vendor">' + escapeHtml(brand) + '</p>' : '') +
          '<h3 class="sz-gear-match__product-title">' + escapeHtml(product.title) + '</h3>' +
          '<p class="sz-gear-match__reason">' + escapeHtml(meta.reason) + '</p>' +
          '<div class="sz-gear-match__price-row">' +
            '<strong>' + formatMoney(price, currency) + '</strong>' +
            (Number(compareAt) > Number(price) ? '<s>' + formatMoney(compareAt, currency) + '</s>' : '') +
          '</div>' +
          '<span class="sz-gear-match__cta">View gear <span aria-hidden="true">→</span></span>' +
        '</div>' +
      '</a>' +
    '</article>';
  };
  const initSection = async (section) => {
    if (!section || section.dataset.szInitialized === 'true') return;
    section.dataset.szInitialized = 'true';

    const productId = section.dataset.productId;
    const limit = Math.max(1, Math.min(6, Number(section.dataset.limit || 4)));
    const currency = section.dataset.currency || window.Shopify?.currency?.active || 'CAD';
    const rail = section.querySelector('[data-sz-gear-match-rail]');
    const logic = section.querySelector('[data-sz-gear-match-logic]');
    const completeBlock = section.querySelector('[data-sz-complete-block]');
    const compareBlock = section.querySelector('[data-sz-compare]');
    const compareGrid = section.querySelector('[data-sz-compare-grid]');
    const current = parseJson(section.querySelector('[data-sz-current-product]'), {});

    if (!productId) return;

    const currentRole = detectRole(current);
    if (!currentRole) return;

    const currentAge = detectAge(current, current.age_group);
    const family = deriveFamily(current.title);
    const searchFamily = deriveSearchFamily(current.title);
    const targetRoles = complementsByRole[currentRole] || [];
    const accessoryRoles = accessoryRolesFor(current, currentRole);

    try {
      const brandedCompareQuery = compareQueryFor(current, currentRole, currentAge, true);
      const broadCompareQuery = compareQueryFor(current, currentRole, currentAge, false);
      const familyRequests = targetRoles.slice(0, limit).map((role) => fetchFamilyRole(family, searchFamily, currentAge, role));
      const accessoryRequests = accessoryRoles.slice(0, limit).map((role) => fetchAccessoryRole(current, role, currentAge));

      const [familyResults, accessoryResults, complementary, related, brandedCompare, broadCompare] = await Promise.all([
        Promise.all(familyRequests),
        Promise.all(accessoryRequests),
        (targetRoles.length || accessoryRoles.length) ? fetchIntent(productId, 'complementary') : Promise.resolve([]),
        fetchIntent(productId, 'related'),
        brandedCompareQuery ? fetchSearchProducts(brandedCompareQuery) : Promise.resolve([]),
        broadCompareQuery && broadCompareQuery !== brandedCompareQuery ? fetchSearchProducts(broadCompareQuery) : Promise.resolve([])
      ]);

      let hasContent = false;

      const coarsePool = mergeUniqueProducts(related, brandedCompare, broadCompare)
        .map(product => ({
          product,
          score: compareScore(product, current, currentRole, currentAge)
        }))
        .filter(item => item.score >= 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 7)
        .map(item => item.product);

      if (coarsePool.length && compareGrid && compareBlock) {
        const enrichedPool = await Promise.all(coarsePool.map(fetchProductDetail));
        const currentComparable = Object.assign({}, current, { _szBrand: current.brand || '' });
        const ranked = enrichedPool
          .map(product => ({ product, score: compareScore(product, currentComparable, currentRole, currentAge) }))
          .filter(item => item.score >= 0)
          .sort((a, b) => b.score - a.score);

        const currentPrice = priceCents(currentComparable);
        const lower = ranked.find(item => priceCents(item.product) > 0 && priceCents(item.product) < currentPrice);
        const higher = ranked.find(item => priceCents(item.product) > currentPrice);
        const chosen = [];
        const chosenKeys = new Set();

        const choose = (item) => {
          if (!item || chosen.length >= 2) return;
          const key = String(item.product?.id || item.product?.handle || item.product?.url || '');
          if (!key || chosenKeys.has(key)) return;
          chosenKeys.add(key);
          chosen.push(item.product);
        };

        if (lower && higher) {
          choose(lower);
          choose(higher);
        } else {
          choose(lower || higher);
          ranked.forEach(choose);
        }

        const allProducts = [currentComparable].concat(chosen);

        compareGrid.innerHTML = allProducts
          .map((item, index) => compareCardMarkup(item, currentComparable, allProducts, index === 0, currency, currentRole))
          .join('');

        compareBlock.hidden = false;
        section.hidden = false;
        hasContent = true;
      }

      const seen = new Set([String(productId)]);
      const selected = [];

      const addProduct = (product, kind, role = '') => {
        if (selected.length >= limit) return false;
        if (!validCandidate(product, productId)) return false;

        if (kind === 'set') {
          if (!matchesAge(product, currentAge)) return false;
          if (role && detectRole(product) !== role) return false;
          product = Object.assign({}, product, { _szBrand: current.brand || '' });
        } else if (kind === 'accessory') {
          if (role && detectRole(product) !== role) return false;
        } else {
          if (currentRole && detectRole(product) === currentRole) return false;
          const candidateAge = detectAge(product);
          if (currentAge && candidateAge && candidateAge !== currentAge) return false;
        }

        const key = String(product.id || product.handle || product.url || '');
        if (!key || seen.has(key)) return false;

        seen.add(key);
        selected.push({ product, kind, role: role || detectRole(product) });
        return true;
      };

      familyResults.forEach((products, index) => {
        const role = targetRoles[index];
        const exact = products.find((product) =>
          validCandidate(product, productId)
          && matchesAge(product, currentAge)
          && deriveFamily(product.title) === family
          && detectRole(product) === role
        );

        if (exact) {
          addProduct(exact, 'set', role);
          return;
        }

        const fallback = products.find((product) =>
          validCandidate(product, productId)
          && matchesAge(product, currentAge)
          && detectRole(product) === role
        );

        if (fallback) addProduct(fallback, 'pair', role);
      });

      accessoryResults.forEach((products, index) => {
        const role = accessoryRoles[index];
        const best = products.find((product) => validCandidate(product, productId) && detectRole(product) === role);
        if (best) addProduct(best, 'accessory', role);
      });

      const allowedRoles = new Set(targetRoles.concat(accessoryRoles));
      complementary.forEach((product) => {
        if (selected.length >= limit) return;
        const role = detectRole(product);
        if (allowedRoles.size && role && !allowedRoles.has(role)) return;
        addProduct(product, 'pair', role);
      });

      if (selected.length && rail && completeBlock) {
        rail.innerHTML = selected
          .map(({ product, kind, role }, index) => cardMarkup(product, kind, index, currency, role, current, currentRole))
          .join('');

        const setCount = selected.filter((item) => item.kind === 'set').length;
        const accessoryCount = selected.filter((item) => item.kind === 'accessory').length;
        const pairCount = selected.filter((item) => item.kind === 'pair').length;

        if (logic) {
          if (setCount && accessoryCount) {
            logic.textContent = 'Matching family pieces first · practical add-ons next';
          } else if (setCount && pairCount) {
            logic.textContent = 'Matching family pieces first · relevant gear next';
          } else if (setCount) {
            logic.textContent = currentAge
              ? 'Same product family · matching ' + ageLabel(currentAge) + ' level'
              : 'Same product family · complementary pieces';
          } else if (accessoryCount) {
            logic.textContent = 'Practical add-ons selected for this product';
          } else {
            logic.textContent = 'Relevant complementary gear only';
          }
        }

        completeBlock.hidden = false;
        section.hidden = false;
        hasContent = true;
      }

      if (!hasContent) section.hidden = true;
    } catch (error) {
      console.error('SportsZone product matching:', error);
    }
  };
  const queuedSections = new WeakSet();
  let gearObserver = null;

  const queueSection = (section) => {
    if (!section || section.dataset.szInitialized === 'true' || queuedSections.has(section)) return;
    queuedSections.add(section);

    const run = () => {
      queuedSections.delete(section);
      initSection(section);
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 900 });
    } else {
      window.setTimeout(run, 0);
    }
  };

  const getObserver = () => {
    if (gearObserver || !('IntersectionObserver' in window)) return gearObserver;

    gearObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        gearObserver.unobserve(entry.target);
        queueSection(entry.target);
      });
    }, {
      root: null,
      rootMargin: '800px 0px',
      threshold: 0
    });

    return gearObserver;
  };

  const boot = (scope = document) => {
    const sections = [];
    if (scope?.matches?.('[data-sz-gear-match]')) sections.push(scope);
    scope.querySelectorAll?.('[data-sz-gear-match]').forEach((section) => sections.push(section));

    const observer = getObserver();
    sections.forEach((section) => {
      if (section.dataset.szInitialized === 'true' || queuedSections.has(section)) return;
      if (observer) observer.observe(section);
      else queueSection(section);
    });
  };

  window.SportsZoneGearMatchBoot = boot;
  boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
